import React, { useState, useRef, useCallback } from 'react';
import { Plus, Trash2, RotateCcw } from 'lucide-react';
import { Button } from '../../../ui/button';

const EDITOR_W = 440;
const EDITOR_H = 130;
const PAD_L = 32;
const PAD_R = 8;
const PAD_T = 12;
const PAD_B = 20;
const GRAPH_W = EDITOR_W - PAD_L - PAD_R;
const GRAPH_H = EDITOR_H - PAD_T - PAD_B;

const MIN_SPEED = 0.1;
const MAX_SPEED = 10;
const GRID_SPEEDS = [0.25, 0.5, 1, 2, 4, 8];
const NODE_R = 5;
const ACCENT = '#a78bfa';

/** Map speed to Y pixel (log scale for better feel) */
const speedToY = (speed) => {
  const logMin = Math.log(MIN_SPEED);
  const logMax = Math.log(MAX_SPEED);
  const norm = (Math.log(Math.max(MIN_SPEED, Math.min(MAX_SPEED, speed))) - logMin) / (logMax - logMin);
  return PAD_T + GRAPH_H * (1 - norm);
};

/** Map Y pixel to speed (log scale) */
const yToSpeed = (y) => {
  const norm = 1 - (y - PAD_T) / GRAPH_H;
  const logMin = Math.log(MIN_SPEED);
  const logMax = Math.log(MAX_SPEED);
  return Math.exp(logMin + norm * (logMax - logMin));
};

/** Map time fraction to X pixel */
const timeToX = (t) => PAD_L + t * GRAPH_W;

/** Map X pixel to time fraction */
const xToTime = (x) => Math.max(0, Math.min(1, (x - PAD_L) / GRAPH_W));

/** Snap to nearest 0.01 */
const snap = (v, min, max) => Math.round(Math.max(min, Math.min(max, v)) * 100) / 100;

const DEFAULT_POINTS = [[0, 1], [0.5, 2], [1, 1]];

export const SpeedRampEditor = ({ points: initialPoints, onChange, duration = 3 }) => {
  const [points, setPoints] = useState(() => {
    const p = initialPoints && initialPoints.length >= 2 ? initialPoints : DEFAULT_POINTS;
    return p.map(([t, s]) => [t, s]);
  });
  const [dragIdx, setDragIdx] = useState(null);
  const [hoverIdx, setHoverIdx] = useState(null);
  const svgRef = useRef(null);

  const sortedPoints = [...points].sort((a, b) => a[0] - b[0]);

  const commitChange = useCallback((newPoints) => {
    const sorted = [...newPoints].sort((a, b) => a[0] - b[0]);
    setPoints(sorted);
    if (onChange) onChange(sorted);
  }, [onChange]);

  const getSVGCoords = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  /* ── Drag handlers ── */
  const handleMouseDown = (e, idx) => {
    e.preventDefault();
    e.stopPropagation();
    setDragIdx(idx);

    const onMove = (me) => {
      const { x, y } = (() => {
        const rect = svgRef.current.getBoundingClientRect();
        return { x: me.clientX - rect.left, y: me.clientY - rect.top };
      })();

      setPoints(prev => {
        const next = prev.map(p => [...p]);
        const isFirst = idx === 0;
        const isLast = idx === prev.length - 1;

        // First & last: lock time at 0/1, only Y changes
        if (isFirst) {
          next[idx] = [0, snap(yToSpeed(y), MIN_SPEED, MAX_SPEED)];
        } else if (isLast) {
          next[idx] = [1, snap(yToSpeed(y), MIN_SPEED, MAX_SPEED)];
        } else {
          const prevT = next[idx - 1]?.[0] ?? 0;
          const nextT = next[idx + 1]?.[0] ?? 1;
          next[idx] = [
            snap(xToTime(x), prevT + 0.02, nextT - 0.02),
            snap(yToSpeed(y), MIN_SPEED, MAX_SPEED),
          ];
        }
        return next;
      });
    };

    const onUp = () => {
      setDragIdx(null);
      setPoints(prev => {
        const sorted = [...prev].sort((a, b) => a[0] - b[0]);
        if (onChange) onChange(sorted);
        return sorted;
      });
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  /* ── Add point on click ── */
  const handleSVGClick = (e) => {
    if (dragIdx !== null) return;
    const { x, y } = getSVGCoords(e);
    if (x < PAD_L || x > PAD_L + GRAPH_W || y < PAD_T || y > PAD_T + GRAPH_H) return;

    const t = snap(xToTime(x), 0.02, 0.98);
    const s = snap(yToSpeed(y), MIN_SPEED, MAX_SPEED);
    const newPts = [...points, [t, s]];
    commitChange(newPts);
  };

  /* ── Delete point on double-click ── */
  const handleDoubleClick = (e, idx) => {
    e.preventDefault();
    e.stopPropagation();
    if (idx === 0 || idx === sortedPoints.length - 1) return; // can't delete endpoints
    const newPts = sortedPoints.filter((_, i) => i !== idx);
    commitChange(newPts);
  };

  /* ── Reset to default ── */
  const handleReset = () => commitChange([...DEFAULT_POINTS]);

  /* ── Calculate effective duration ── */
  const effDur = (() => {
    let total = 0;
    for (let i = 1; i < sortedPoints.length; i++) {
      const segFrac = sortedPoints[i][0] - sortedPoints[i - 1][0];
      const avgSpeed = (sortedPoints[i][1] + sortedPoints[i - 1][1]) / 2;
      total += (duration * segFrac) / Math.max(0.1, avgSpeed);
    }
    return total;
  })();

  /* ── Build SVG path ── */
  const lineCoords = sortedPoints.map(([t, s]) => [timeToX(t), speedToY(s)]);
  const lineD = lineCoords.map((c, i) => `${i === 0 ? 'M' : 'L'} ${c[0].toFixed(1)},${c[1].toFixed(1)}`).join(' ');
  const fillD = `M ${PAD_L},${PAD_T + GRAPH_H} ${lineCoords.map(c => `L ${c[0].toFixed(1)},${c[1].toFixed(1)}`).join(' ')} L ${PAD_L + GRAPH_W},${PAD_T + GRAPH_H} Z`;

  return (
    <div className="border border-purple-500/30 rounded-lg bg-black/20 p-2 space-y-1.5" data-testid="speed-ramp-editor">
      {/* Header */}
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-medium text-purple-300">Custom Speed Curve</span>
          <span className="text-[9px] text-muted-foreground">{sortedPoints.length} keyframes</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[9px] font-mono text-purple-300">{effDur.toFixed(1)}s eff</span>
          <Button variant="ghost" size="icon" className="h-5 w-5" onClick={handleReset} title="Reset to default" data-testid="ramp-editor-reset">
            <RotateCcw className="h-3 w-3 text-muted-foreground" />
          </Button>
        </div>
      </div>

      {/* SVG Graph */}
      <svg
        ref={svgRef}
        width={EDITOR_W}
        height={EDITOR_H}
        className="cursor-crosshair select-none rounded"
        onClick={handleSVGClick}
        data-testid="ramp-editor-graph"
      >
        {/* Background */}
        <rect x={PAD_L} y={PAD_T} width={GRAPH_W} height={GRAPH_H} fill="rgba(0,0,0,0.3)" rx={4} />

        {/* Grid lines + speed labels */}
        {GRID_SPEEDS.map(s => {
          const y = speedToY(s);
          if (y < PAD_T || y > PAD_T + GRAPH_H) return null;
          return (
            <g key={s}>
              <line x1={PAD_L} y1={y} x2={PAD_L + GRAPH_W} y2={y} stroke="rgba(255,255,255,0.08)" strokeDasharray="3,3" />
              <text x={PAD_L - 4} y={y + 3} textAnchor="end" fontSize={8} fill="rgba(255,255,255,0.35)" fontFamily="monospace">{s}x</text>
            </g>
          );
        })}

        {/* Time labels */}
        {[0, 0.25, 0.5, 0.75, 1].map(t => (
          <text key={t} x={timeToX(t)} y={PAD_T + GRAPH_H + 13} textAnchor="middle" fontSize={7} fill="rgba(255,255,255,0.3)" fontFamily="monospace">
            {Math.round(t * 100)}%
          </text>
        ))}

        {/* Filled area under curve */}
        <path d={fillD} fill={ACCENT} opacity={0.12} />

        {/* Curve line */}
        <path d={lineD} fill="none" stroke={ACCENT} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />

        {/* Keyframe nodes */}
        {sortedPoints.map(([t, s], i) => {
          const cx = timeToX(t);
          const cy = speedToY(s);
          const isEndpoint = i === 0 || i === sortedPoints.length - 1;
          const isHover = hoverIdx === i;
          const isDrag = dragIdx === i;

          return (
            <g key={i}>
              {/* Larger hit area */}
              <circle
                cx={cx} cy={cy} r={12} fill="transparent" className="cursor-grab"
                onMouseDown={(e) => handleMouseDown(e, i)}
                onDoubleClick={(e) => handleDoubleClick(e, i)}
                onMouseEnter={() => setHoverIdx(i)}
                onMouseLeave={() => setHoverIdx(null)}
                data-testid={`ramp-keyframe-${i}`}
              />
              {/* Visual node */}
              <circle
                cx={cx} cy={cy}
                r={isDrag ? NODE_R + 2 : isHover ? NODE_R + 1 : NODE_R}
                fill={isEndpoint ? '#6d28d9' : ACCENT}
                stroke="white"
                strokeWidth={isDrag ? 2 : 1.5}
                className="pointer-events-none transition-all"
                style={{ filter: isDrag ? 'drop-shadow(0 0 4px rgba(167,139,250,0.6))' : 'none' }}
              />
              {/* Speed label on hover/drag */}
              {(isHover || isDrag) && (
                <text x={cx} y={cy - NODE_R - 5} textAnchor="middle" fontSize={8} fill="white" fontFamily="monospace" className="pointer-events-none">
                  {s.toFixed(1)}x
                </text>
              )}
            </g>
          );
        })}

        {/* Click-to-add hint */}
        <text x={PAD_L + GRAPH_W / 2} y={PAD_T + GRAPH_H - 4} textAnchor="middle" fontSize={7} fill="rgba(255,255,255,0.15)" className="pointer-events-none">
          Click to add · Double-click node to delete
        </text>
      </svg>
    </div>
  );
};
